import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Mic } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

export default function ArtistsDirectoryPage() {
    const [artists, setArtists] = useState(null);
    const [q, setQ] = useState("");
    const [genre, setGenre] = useState("ALL");

    useEffect(() => {
        api.get("/artists/approved", { params: { limit: 200 } })
            .then((r) => setArtists(r.data || []))
            .catch(() => setArtists([]));
    }, []);

    const genres = useMemo(() => {
        if (!artists) return [];
        const set = new Set(artists.map((a) => (a.genre || "").trim()).filter(Boolean));
        return ["ALL", ...Array.from(set).sort()];
    }, [artists]);

    const filtered = useMemo(() => {
        if (!artists) return [];
        const needle = q.trim().toLowerCase();
        return artists.filter((a) => {
            if (genre !== "ALL" && (a.genre || "").toLowerCase() !== genre.toLowerCase()) return false;
            if (!needle) return true;
            return (
                (a.artist_name || "").toLowerCase().includes(needle) ||
                (a.location || "").toLowerCase().includes(needle) ||
                (a.genre || "").toLowerCase().includes(needle)
            );
        });
    }, [artists, q, genre]);

    return (
        <div className="bg-black text-white min-h-screen">
            <SEO
                title="Artists Directory"
                description="Approved Afrobeats and street-culture artists featured on MyStreetzMag. Discover new voices, listen to their music, and follow their social channels."
                type="website"
                ogSlug="page:artists"
                keywords={["Afrobeats artists", "Naija music", "street culture", "MyStreetzMag artists"]}
            />
            <Header />
            <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-12">
                {/* Hero */}
                <section className="border-b border-streetz-line pb-8 mb-10">
                    <span className="overline text-streetz-yellow">DIRECTORY</span>
                    <h1 data-testid="artists-directory-title" className="font-display uppercase text-5xl md:text-7xl leading-none mt-3">
                        The Artists
                    </h1>
                    <p className="text-streetz-muted mt-4 max-w-2xl">
                        Every approved artist on MyStreetzMag, in one place. Real submissions, vetted by our editors, sourced from the streets.
                    </p>
                    <Link
                        to="/submit"
                        data-testid="artists-directory-submit-cta"
                        className="mt-6 inline-flex items-center gap-2 bg-streetz-red text-white overline px-5 py-3 hover:bg-white hover:text-black transition-colors"
                    >
                        <Mic className="w-4 h-4" /> SUBMIT YOUR MUSIC
                    </Link>
                </section>

                {/* Filters */}
                <section className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
                    <label className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-streetz-muted" />
                        <input
                            type="text"
                            data-testid="artists-directory-search-input"
                            placeholder="Search by name, location or genre…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full bg-transparent border border-streetz-line pl-10 pr-4 py-3 text-white placeholder:text-streetz-muted focus:outline-none focus:border-streetz-red"
                        />
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                        {genres.map((g) => (
                            <button
                                key={g}
                                type="button"
                                data-testid={`artists-directory-filter-${g.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setGenre(g)}
                                className={`overline px-3 py-2 border transition-colors ${
                                    genre === g
                                        ? "bg-streetz-red text-white border-streetz-red"
                                        : "border-streetz-line text-white hover:border-streetz-red"
                                }`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                </section>

                {/* Grid */}
                {artists === null ? (
                    <div className="text-streetz-muted py-20 text-center">Loading artists…</div>
                ) : filtered.length === 0 ? (
                    <div
                        data-testid="artists-directory-empty"
                        className="border border-streetz-line p-12 text-center"
                    >
                        <p className="font-display uppercase text-3xl mb-2">No artists yet</p>
                        <p className="text-streetz-muted">
                            {artists.length === 0
                                ? "Once submissions get approved, they'll appear here."
                                : "No artists match your search. Try a different name or genre."}
                        </p>
                    </div>
                ) : (
                    <section
                        data-testid="artists-directory-grid"
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
                    >
                        {filtered.map((a) => (
                            <Link
                                key={a.id}
                                to={`/artist/${a.id}`}
                                data-testid={`artist-card-${a.id}`}
                                className="group block border border-streetz-line hover:border-streetz-red transition-colors"
                            >
                                <div className="aspect-square overflow-hidden bg-streetz-paper">
                                    {a.photo_url ? (
                                        <img
                                            src={a.photo_url}
                                            alt={a.artist_name}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full grid place-items-center font-display text-6xl text-streetz-line">
                                            {a.artist_name?.[0]}
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 md:p-4">
                                    <span className="overline text-streetz-yellow">{a.genre || "Artist"}</span>
                                    <h3 className="font-display uppercase text-lg md:text-xl leading-tight mt-1 group-hover:text-streetz-red transition-colors line-clamp-2">
                                        {a.artist_name}
                                    </h3>
                                    {a.location && (
                                        <p className="text-streetz-muted text-xs md:text-sm mt-1 truncate">{a.location}</p>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </section>
                )}

                {artists !== null && filtered.length > 0 && (
                    <p
                        data-testid="artists-directory-count"
                        className="mt-10 text-streetz-muted text-sm overline"
                    >
                        Showing {filtered.length} of {artists.length} artists
                    </p>
                )}
            </main>
            <Footer />
        </div>
    );
}
